import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { change, Field, reduxForm } from 'redux-form';

import { useTranslation, withTranslation } from 'providers/GILT.provider';
import asObject from 'utils/objects/asObject.util';

import Button from 'settings/page-elements/Button';
import { addProduct, editProduct, resetSavedProperty } from 'settings/product-listing/actions';
import SavingStatus from 'settings/product-listing/components/SavingStatus';

import 'styles/common/confirmation-modal.scss';

const InputField = ({ input, meta, reset, ...rest }) => {
  const { name, value } = asObject(input);
  const { touched, error, onFocus, active, form, dispatch } = asObject(meta);

  const className = `${touched && error && !active ? 'has-error' : ''}`;
  const resetField = () => void dispatch(change(form, name, ''));

  const handleClick = () => resetField();
  const handleKeyUp = () => null;

  return (
    <div data-bem="InputField--settings">
      <div className="input-clear-container">
        <input type="text" className={`${className} utl-full-width`} onFocus={onFocus} {...input} {...rest} />
        {value !== '' && (
          <span
            className="clear-value animated bounceIn"
            onClick={handleClick}
            onKeyUp={handleKeyUp}
            role="button"
            tabIndex="0"
          />
        )}
      </div>

      <div className="input-prompt-show">
        <div className="input-baseline" />
        <span> {touched ? error : ''}</span>
      </div>
    </div>
  );
};

const ProductFormWithSubmit = ({
  addProduct,
  formType,
  editProduct,
  productId,
  salonId,
  cancel,
  reset,
  productsList,
  resetSavedProperty,
  handleSubmit,
}) => {
  const { t } = useTranslation();

  const onSubmit = useCallback(
    (values) => {
      const { title, brand, sub_brand: subBrand, price, quantityInStock, barcode } = values;

      if (formType === 'edit') {
        return void editProduct(values, productId);
      }

      if (formType !== 'create') return void 1;

      return void addProduct(
        salonId,
        title,
        brand,
        subBrand,
        price && parseFloat(price).toFixed(2),
        barcode, // should be in this order
        quantityInStock && parseFloat(quantityInStock),
      );
    },
    [addProduct, editProduct, formType, productId, salonId],
  );

  const { isPosting, isSaved } = asObject(productsList);

  const buttonName = useMemo(() => {
    if (isPosting) {
      return t('common.saving');
    }
    if (isSaved) {
      return t('common.saved');
    }

    return formType === 'edit'
      ? t('stocks.managerLayout.addProductModal.editItemButton')
      : t('stocks.managerLayout.addProductModal.addItemButton');
  }, [formType, isPosting, isSaved, t]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="modal-content">
        <div className="row form-horizontal">
          <div className="col-sm-7">
            <label htmlFor="title">{t('stocks.managerLayout.addProductModal.productName')}</label>
            <Field
              name="title"
              placeholder={t('stocks.managerLayout.addProductModal.productName')}
              type="text"
              component={InputField}
            />
          </div>
          <div className="col-sm-5">
            <label htmlFor="brand">{t('stocks.managerLayout.addProductModal.brand')}</label>
            <Field name="brand" type="text" placeholder="" component={InputField} />
          </div>
          <div className="col-sm-5">
            <label htmlFor="sub_brand">{t('stocks.managerLayout.addProductModal.subBrand')}</label>
            <Field name="sub_brand" type="text" placeholder="" component={InputField} />
          </div>
          <div className="col-sm-5">
            <label htmlFor="price">{t('stocks.managerLayout.addProductModal.retailPrice')}</label>
            <Field name="price" type="text" placeholder="0.00" component={InputField} />
          </div>
          <div className="col-sm-7">
            <label htmlFor="quantityInStock">{t('stocks.managerLayout.addProductModal.stockQuantity')}</label>
            <Field
              name="quantityInStock"
              type="text"
              placeholder={t('stocks.managerLayout.addProductModal.stockQuantity')}
              component={InputField}
            />
          </div>
          <div className="col-sm-7">
            <label htmlFor="barcode">{t('stocks.managerLayout.addProductModal.barcode')}</label>
            <Field name="barcode" type="text" placeholder="" component={InputField} />
          </div>
          <SavingStatus
            productsList={productsList}
            cancel={cancel}
            reset={reset}
            resetSavedProperty={resetSavedProperty}
          />
          <p className="add-product-notes" />
        </div>
      </div>

      <div className="modal-footer">
        <Button className="btn btn-violet-link" buttonName={t('common.buttons.cancel')} onClickButton={cancel} />
        <button className="btn btn-violet-link" type="submit">
          {buttonName}
        </button>
      </div>
    </form>
  );
};

const validate = (values, props) => {
  const { t } = props;
  const errors = {};
  if (!values.title || values.title.length < 3) {
    errors.title = t('stocks.managerLayout.addProductModal.productNameErrMsg');
  }
  return errors;
};

const mapStateToProps = (state) => ({ productsList: state.settings.products });

const mapDispatchToProps = (dispatch) => ({
  // eslint-disable-next-line camelcase
  addProduct: (salonId, title, brand, sub_brand, price, quantityInStock, barcode) =>
    dispatch(addProduct(salonId, title, brand, sub_brand, price, barcode, quantityInStock, barcode)),
  resetSavedProperty: () => dispatch(resetSavedProperty()),
  editProduct: (product, productId) => dispatch(editProduct(product, productId)),
});

const ProductFormWithReduxForm = reduxForm({
  validate,
  form: 'ProductForm',
})(ProductFormWithSubmit);

const ProductForm = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ProductFormWithReduxForm));

const ProductModal = ({ cancel, formType, popupTitle, product, productId, salonId }) => (
  <div data-bem="ProductModal">
    <div className="confirmation-modal animated zoomIn">
      <div className="modal-body">
        <h2 className="modal-title"> {popupTitle}</h2>
        <ProductForm
          initialValues={product || null}
          productId={productId || null}
          cancel={cancel}
          salonId={salonId}
          formType={formType}
        />
      </div>
    </div>
  </div>
);

ProductModal.defaultProps = {
  product: null,
  productId: 0,
};

// this extra export is needed for tests that provide their own mock for connect()
export { InputField as InputField4Test };
export { ProductFormWithReduxForm as ProductForm4Test };

export default ProductModal;
